(() => {
  if (window.proactiveReviewsScriptInitialized) return;

  window.proactiveReviewsScriptInitialized = true;
  const rootSelector = '[data-proactive-reviews]';

  const activateTab = (root, activeTab, moveFocus = false) => {
    const activeId = activeTab.dataset.proactiveTab;

    root.querySelectorAll('[data-proactive-tab]').forEach((tab) => {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    root.querySelectorAll('[data-proactive-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.proactivePanel !== activeId;
    });

    if (moveFocus) activeTab.focus();
  };

  const initializeTabs = (root) => {
    const tabs = [...root.querySelectorAll('[data-proactive-tab]')];

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(root, tab));
      tab.addEventListener('keydown', (event) => {
        let nextIndex = index;

        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          activateTab(root, tabs[nextIndex], true);
        }
      });
    });
  };

  const initializeSlider = (slider) => {
    const slides = [...slider.querySelectorAll('[data-scroll-slide]')];
    const dotGroups = [...slider.parentElement.querySelectorAll(':scope > [data-scroll-dots]')];
    let frameId;
    let dragMoved = false;
    let dragStartScrollLeft = 0;
    let dragStartX = 0;
    let isMouseDragging = false;

    if (slides.length < 2 || dotGroups.length === 0) return;

    const getScrollTarget = (pageIndex, slidesPerPage) => {
      const firstSlideOffset = slides[0].offsetLeft;
      const targetSlide = slides[Math.min(pageIndex * slidesPerPage, slides.length - 1)];
      const maximumScroll = slider.scrollWidth - slider.clientWidth;

      return Math.min(targetSlide.offsetLeft - firstSlideOffset, maximumScroll);
    };

    const getActivePageIndex = (group) => {
      const dots = [...group.querySelectorAll('[data-scroll-dot]')];
      const slidesPerPage = Number(group.dataset.slidesPerPage) || 1;

      return dots.reduce((closestIndex, dot, index) => {
        const currentDistance = Math.abs(slider.scrollLeft - getScrollTarget(index, slidesPerPage));
        const closestDistance = Math.abs(slider.scrollLeft - getScrollTarget(closestIndex, slidesPerPage));
        return currentDistance < closestDistance ? index : closestIndex;
      }, 0);
    };

    const setActiveDots = () => {
      dotGroups.forEach((group) => {
        const activeDotIndex = getActivePageIndex(group);

        group.querySelectorAll('[data-scroll-dot]').forEach((dot, index) => {
          const isActive = index === activeDotIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      });
    };

    const updateActiveDot = () => {
      setActiveDots();
    };

    slider.addEventListener('scroll', () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updateActiveDot);
    }, { passive: true });

    dotGroups.forEach((group) => {
      const slidesPerPage = Number(group.dataset.slidesPerPage) || 1;

      group.querySelectorAll('[data-scroll-dot]').forEach((dot, index) => {
        dot.addEventListener('click', () => {
          slider.scrollTo({ left: getScrollTarget(index, slidesPerPage), behavior: 'smooth' });
        });
      });
    });

    slider.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;

      dragMoved = false;
      dragStartX = event.clientX;
      dragStartScrollLeft = slider.scrollLeft;
      isMouseDragging = true;
      slider.classList.add('is-dragging');
    });

    window.addEventListener('mousemove', (event) => {
      if (!isMouseDragging) return;

      const distance = event.clientX - dragStartX;
      dragMoved = dragMoved || Math.abs(distance) > 4;
      slider.scrollLeft = dragStartScrollLeft - distance;
      event.preventDefault();
    });

    const finishDragging = () => {
      if (!isMouseDragging) return;

      isMouseDragging = false;
      slider.classList.remove('is-dragging');

      if (!dragMoved) return;

      const visibleDotGroup = dotGroups.find((group) => getComputedStyle(group).display !== 'none');
      if (!visibleDotGroup) return;

      const slidesPerPage = Number(visibleDotGroup.dataset.slidesPerPage) || 1;
      const activePageIndex = getActivePageIndex(visibleDotGroup);
      slider.scrollTo({ left: getScrollTarget(activePageIndex, slidesPerPage), behavior: 'smooth' });
      setTimeout(() => {
        dragMoved = false;
      });
    };

    window.addEventListener('mouseup', finishDragging);
    slider.addEventListener('click', (event) => {
      if (!dragMoved) return;

      event.preventDefault();
      event.stopPropagation();
      dragMoved = false;
    }, true);

    new ResizeObserver(updateActiveDot).observe(slider);
  };

  const initializeVideos = (root) => {
    root.querySelectorAll('.proactive-reviews__video-item').forEach((item) => {
      const playbackButton = item.querySelector('[data-video-toggle]');
      const muteButton = item.querySelector('[data-video-mute-toggle]');
      const video = item?.querySelector('video');

      if (!video || !playbackButton || !muteButton) return;

      const updatePlaybackState = () => {
        const isPlaying = !video.paused && !video.ended;
        item.classList.toggle('is-playing', isPlaying);
        playbackButton.setAttribute('aria-label', isPlaying ? root.dataset.pauseVideoLabel : root.dataset.playVideoLabel);
        playbackButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      };

      const updateMuteState = () => {
        item.classList.toggle('is-muted', video.muted);
        muteButton.setAttribute('aria-label', video.muted ? root.dataset.unmuteVideoLabel : root.dataset.muteVideoLabel);
        muteButton.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
      };

      const muteOtherVideos = () => {
        document.querySelectorAll('.proactive-reviews__video').forEach((otherVideo) => {
          if (otherVideo !== video) otherVideo.muted = true;
        });
      };

      playbackButton.addEventListener('click', () => {
        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });

      muteButton.addEventListener('click', () => {
        if (video.muted) {
          muteOtherVideos();
          video.muted = false;
        } else {
          video.muted = true;
        }
        updateMuteState();
      });

      video.addEventListener('play', updatePlaybackState);
      video.addEventListener('pause', updatePlaybackState);
      video.addEventListener('ended', updatePlaybackState);
      video.addEventListener('volumechange', () => {
        if (!video.muted) muteOtherVideos();
        updateMuteState();
      });

      video.muted = true;
      video.defaultMuted = true;
      updatePlaybackState();
      updateMuteState();
      video.play().catch(() => {});
    });
  };

  const initializeProductScroll = (root) => {
    const button = root.querySelector('[data-scroll-to-product]');

    if (!button) return;

    button.addEventListener('click', () => {
      const laborCta = document.querySelector('.labor-cta__button');

      if (laborCta && !laborCta.disabled) {
        laborCta.click();
        return;
      }

      const product = document.querySelector('#main-product') || document.querySelector('[id^="MainProduct-"]');

      if (!product) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      product.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    });
  };

  const initializeSection = (root) => {
    if (root.dataset.proactiveReviewsInitialized === 'true') return;

    root.dataset.proactiveReviewsInitialized = 'true';
    initializeTabs(root);
    root.querySelectorAll('[data-scroll-slider]').forEach(initializeSlider);
    initializeVideos(root);
    initializeProductScroll(root);
  };

  document.querySelectorAll(rootSelector).forEach(initializeSection);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(rootSelector).forEach(initializeSection);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest(rootSelector);
    const tab = root?.querySelector(`[data-proactive-tab="${event.detail?.blockId}"]`);
    if (root && tab) activateTab(root, tab);
  });
})();
